<template>
  <div class="featured-news">
    <div class="featured-news__header flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
      <div class="featured-news__headline flex flex-col gap-6">
        <h2 class="featured-news__title">{{ component.featuredNewsAndEventsTitle }}</h2>
        <p class="featured-news__description">{{ component.featuredNewsAndEventsDescription }}</p>
      </div>
      <div class="featured-news__ctas flex gap-4 justify-between lg:flex-col lg:items-end">
        <SharedButton :label="t('components.featured-news.cta')" class="w-max" variant="primary" :to="getLocaleNewsLink()" />
        <div class="related-articles__swiper-actions flex items-center gap-4 lg:hidden">
          <SharedIconButton
            :aria-label="t('swiper.previous')"
            :title="t('swiper.previous')"
            variant="accent"
            @click="() => swiper?.prev()"
            icon="arrow-left"
            class="w-10 h-10"
            ref="swiperBackButton"
            ><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12.7069 17.293L8.41394 13H17.9999V11H8.41394L12.7069 6.70697L11.2929 5.29297L4.58594 12L11.2929 18.707L12.7069 17.293Z" fill="currentColor" />
            </svg>
          </SharedIconButton>
          <SharedIconButton
            :aria-label="t('swiper.next')"
            :title="t('swiper.next')"
            variant="accent"
            @click="() => swiper?.next()"
            icon="arrow-right"
            class="w-10 h-10"
            ref="swiperNextButton"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M11.2931 17.293L15.5861 13H6.00006V11H15.5861L11.2931 6.70697L12.7071 5.29297L19.4141 12L12.7071 18.707L11.2931 17.293Z" fill="currentColor" />
            </svg>
          </SharedIconButton>
        </div>
      </div>
    </div>
    <div class="featured-news__swiper relative mt-12 lg:mt-20 z-0">
      <div class="featured-news__swiper-actions hidden lg:flex items-center justify-between z-10 gap-4 pointer-events-none absolute top-1/2 -translate-y-1/2 w-full">
        <SharedIconButton
          :aria-label="t('swiper.previous')"
          :title="t('swiper.previous')"
          variant="accent"
          @click="() => swiper?.prev()"
          icon="arrow-left"
          class="swiper-button-prev w-10 h-10 pointer-events-auto relative"
          ref="swiperBackButton"
          ><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12.7069 17.293L8.41394 13H17.9999V11H8.41394L12.7069 6.70697L11.2929 5.29297L4.58594 12L11.2929 18.707L12.7069 17.293Z" fill="currentColor" />
          </svg>
        </SharedIconButton>
        <SharedIconButton
          :aria-label="t('swiper.next')"
          :title="t('swiper.next')"
          variant="accent"
          @click="() => swiper?.next()"
          icon="arrow-right"
          class="swiper-button-next w-10 h-10 pointer-events-auto relative"
          ref="swiperNextButton"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M11.2931 17.293L15.5861 13H6.00006V11H15.5861L11.2931 6.70697L12.7071 5.29297L19.4141 12L12.7071 18.707L11.2931 17.293Z" fill="currentColor" />
          </svg>
        </SharedIconButton>
      </div>
      <ClientOnly>
        <swiper-container
          :slides-per-view="1"
          :space-between="32"
          :breakpoints="{
            768: {
              slidesPerView: 2,
              spaceBetween: 32,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 48,
            },
          }"
          ref="swiperContainer"
        >
          <swiper-slide
            v-for="(item, index) in featuredItems"
            :key="index"
            class="featured-news__swiper-slide flex flex-col h-auto border border-accent rounded-md overflow-hidden z-0"
            :class="{ 'featured-news__event-card': item.event, 'featured-news__news-card': item.news }"
          >
            <div class="featured-news__img-container h-[270px] w-full overflow-hidden relative bg-white">
              <SharedPicture
                :media="getItemMedia(item)"
                :alt="getItemMedia(item)?.alternativeText || item.event ? item.event?.title : item.news?.title"
                class="featured-news__swiper-slide-image w-full object-cover object-center h-full"
                width="371"
                height="270"
                format="webp"
                :quality="85"
              />
              <div class="featured-news__type-pill absolute bottom-3 left-3 px-4 py-2 text-small-semibold bg-white border border-christine rounded-full">
                {{ item.event ? t('components.featured-news.event') : t('components.featured-news.news') }}
              </div>
            </div>
            <div class="featured-news__swiper-slide-content flex flex-col gap-2 p-6 min-h-0 flex-1 bg-white">
              <NuxtLink
                :to="item.event ? getLocaleEventLink(item.event.title) : getLocaleNewsLink(item.news!.slug)"
                :aria-label="item.event ? t('components.featured-news.readMoreEvent') : t('components.featured-news.readMoreNews')"
                :title="item.event ? t('components.featured-news.readMoreEvent') : t('components.featured-news.readMoreNews')"
              >
                <h3 class="h5 featured-news__swiper-slide-title">
                  {{ item.event ? item.event.title : item.news?.title }}
                </h3>
              </NuxtLink>
              <div class="featured-news__swiper-content text-regular-normal">
                <p v-if="item.news" class="featured-news__excerpt">
                  {{ item.news.excerpt }}
                </p>
                <div v-else class="featured-news__event-data flex flex-col gap-4 mt-2">
                  <div class="featured-news__event-date flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" class="size-6 min-w-6">
                      <path
                        d="M7 11.7749H9V13.7749H7V11.7749ZM7 15.7749H9V17.7749H7V15.7749ZM11 11.7749H13V13.7749H11V11.7749ZM11 15.7749H13V17.7749H11V15.7749ZM15 11.7749H17V13.7749H15V11.7749ZM15 15.7749H17V17.7749H15V15.7749Z"
                        fill="#37B5AA"
                      />
                      <path
                        d="M5 22.7749H19C20.103 22.7749 21 21.8779 21 20.7749V6.7749C21 5.6719 20.103 4.7749 19 4.7749H17V2.7749H15V4.7749H9V2.7749H7V4.7749H5C3.897 4.7749 3 5.6719 3 6.7749V20.7749C3 21.8779 3.897 22.7749 5 22.7749ZM19 8.7749L19.001 20.7749H5V8.7749H19Z"
                        fill="#37B5AA"
                      />
                    </svg>
                    <time
                      class="featured-news__event-date-text text-regular-normal"
                      :datetime="item.event?.endDate ? `${item.event.startDate}/${item.event.endDate}` : item.event?.startDate"
                    >
                      {{ formatEventDateTime(item.event) }}
                    </time>
                  </div>
                  <div class="featured-news__event-date flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" class="size-6 min-w-6">
                      <path
                        d="M12.0001 14.7749C14.2061 14.7749 16.0001 12.9809 16.0001 10.7749C16.0001 8.5689 14.2061 6.7749 12.0001 6.7749C9.79406 6.7749 8.00006 8.5689 8.00006 10.7749C8.00006 12.9809 9.79406 14.7749 12.0001 14.7749ZM12.0001 8.7749C13.1031 8.7749 14.0001 9.6719 14.0001 10.7749C14.0001 11.8779 13.1031 12.7749 12.0001 12.7749C10.8971 12.7749 10.0001 11.8779 10.0001 10.7749C10.0001 9.6719 10.8971 8.7749 12.0001 8.7749Z"
                        fill="#37B5AA"
                      />
                      <path
                        d="M11.4201 22.5889C11.5893 22.7098 11.7921 22.7747 12.0001 22.7747C12.2081 22.7747 12.4108 22.7098 12.5801 22.5889C12.8841 22.3739 20.0291 17.2149 20.0001 10.7749C20.0001 6.3639 16.4111 2.7749 12.0001 2.7749C7.58909 2.7749 4.00009 6.3639 4.00009 10.7699C3.97109 17.2149 11.1161 22.3739 11.4201 22.5889ZM12.0001 4.7749C15.3091 4.7749 18.0001 7.4659 18.0001 10.7799C18.0211 15.2179 13.6121 19.2029 12.0001 20.5099C10.3891 19.2019 5.97909 15.2159 6.00009 10.7749C6.00009 7.4659 8.69109 4.7749 12.0001 4.7749Z"
                        fill="#37B5AA"
                      />
                    </svg>
                    <address class="featured-news__event-date-text text-regular-normal not-italic">
                      {{ item.event?.venue }}
                    </address>
                  </div>
                </div>
              </div>

              <NuxtLink
                :to="item.event ? getLocaleEventLink(item.event.title) : getLocaleNewsLink(item.news!.slug)"
                class="featured-news__swiper-slide-link flex items-center gap-2 mt-auto pt-4"
                :aria-label="t('menu.navigateTo', { label: item.event?.title || item.news?.title || '' })"
                :title="t('menu.navigateTo', { label: item.event?.title || item.news?.title || '' })"
              >
                <span class="featured-news__swiper-slide-link-text">{{
                  item.event ? t('components.featured-news.readMoreEvent') : t('components.featured-news.readMoreNews')
                }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                  <path d="M9.70697 17.7245L15.414 12.0175L9.70697 6.31055L8.29297 7.72455L12.586 12.0175L8.29297 16.3105L9.70697 17.7245Z" fill="#37B5AA" />
                </svg>
              </NuxtLink>
            </div>
          </swiper-slide>
        </swiper-container>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { SwiperContainer } from 'swiper/element';
  import type { GraphQLFeaturedNewsAndEvents } from '~/types/graphql';
  import type { NewsArticlesQueryParams, NewsArticlesResponse, EventArchive } from '~/types/api';

  const { t, locale } = useI18n();

  const props = defineProps<{
    component: GraphQLFeaturedNewsAndEvents;
    index: number;
  }>();

  const { data: news } = await useAsyncData(
    `featured-news-${locale.value}`,
    async () => {
      const queryParams: NewsArticlesQueryParams = {
        page: 1,
        pageSize: props.component.itemsToShow - 1,
        locale: locale.value,
        slugs: [],
      };

      if (props.component.featuredSelectedNews) {
        queryParams.slugs = props.component.featuredSelectedNews.map(item => item.slug);
      }

      const response = await $fetch<NewsArticlesResponse>('/api/news-archive', { query: queryParams });

      return response;
    },
    {
      watch: [locale],
    }
  );

  const { data: events } = await useAsyncData<EventArchive>(
    `featured-events-${locale.value}`,
    () =>
      $fetch('/api/events-archive', {
        params: {
          locale: locale.value,
        },
      }),
    {
      watch: [locale],
    }
  );

  const featuredItems = computed(() => {
    const items = [];
    if (news.value?.data.length) {
      items.push({ news: news.value.data[0] });
    }
    if (events.value?.events.length) {
      items.push({ event: events.value.events[0] });
    }

    items.push(...(news.value?.data.slice(1) || []).map(item => ({ news: item })));

    return items;
  });

  const swiperContainer = ref<SwiperContainer | null>(null);
  const swiper = useSwiper(swiperContainer, {});

  const { getLocaleNewsLink, getLocaleEventLink } = useCustomLocaleRoute();

  const getItemMedia = (item: (typeof featuredItems.value)[0]) => {
    if (item.event) {
      return item.event.image;
    }
    return item.news?.featuredImage;
  };

  const formatEventDateTime = (event: any) => {
    if (!event?.startDate) return '';

    const startDate = new Date(event.startDate);
    const endDate = event.endDate ? new Date(event.endDate) : null;

    // Extract time from datetime strings
    const startTime = startDate.toTimeString().slice(0, 5); // HH:mm
    const endTime = endDate ? endDate.toTimeString().slice(0, 5) : null;

    // Format dates
    const startDateFormatted = startDate.toLocaleDateString(locale.value, {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    });
    const endDateFormatted =
      endDate && endDate.toDateString() !== startDate.toDateString()
        ? endDate.toLocaleDateString(locale.value, {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
          })
        : null;

    // Build the string
    let result = startDateFormatted;
    if (endDateFormatted) {
      result += ` - ${endDateFormatted}`;
    }
    if (startTime || endTime) {
      result += ` | ${startTime}`;
      if (endTime && endTime !== startTime) {
        result += ` - ${endTime}`;
      }
    }

    return result;
  };
</script>

<style lang="scss" scoped>
  .featured-news__swiper-slide {
    @apply relative mt-[5px] mb-[2px] translate-y-0;
    // Only transition `transform` (the hover lift below). Using `all` also animated the
    // inline `width`/`margin-right` that Swiper writes on each slide → non-composited
    // layout animation flagged by Lighthouse + CLS. `transform` is compositor-friendly.
    transition: transform 0.3s ease-in-out;

    .featured-news__swiper-slide-link {
      @apply transition-all duration-150;
    }

    &:hover {
      @apply -translate-y-[5px];
      .featured-news__swiper-slide-link {
        @apply text-accent underline underline-offset-4;
      }
    }
  }
</style>
<style lang="scss">
  .featured-news {
    .swiper-button-prev {
      transform: translateX(-50%);
      @media (min-width: 1385px) {
        transform: translateX(-150%);
      }
      @screen 2xl {
        transform: translateX(-200%);
      }
      &:after {
        display: none !important;
      }
    }
    .swiper-button-next {
      transform: translateX(50%);
      @media (min-width: 1385px) {
        transform: translateX(150%);
      }
      @screen 2xl {
        transform: translateX(200%);
      }
      &:after {
        display: none !important;
      }
    }
  }
</style>
